import React, { useState } from "react";
import { Button, Space, Typography } from "antd";
import { SearchOutlined, PlusOutlined } from "@ant-design/icons";
import { ModernSearchModal } from "./ModernSearchModal";

const { Text } = Typography;

interface SearchSelectButtonProps<T> {
  value?: any;
  onChange?: (value: any, item: T) => void;
  resource: string;
  title: string;
  placeholder?: string;
  searchFields: string[];
  renderItem: (item: T) => {
    title: string;
    description?: string;
    meta?: string;
    avatar?: React.ReactNode;
  };
  displayValue?: string;
  disabled?: boolean;
  size?: "small" | "middle" | "large";
  style?: React.CSSProperties;
  allowClear?: boolean;
  onClear?: () => void;
}

export const SearchSelectButton = <T extends Record<string, any>>({
  value,
  onChange,
  resource,
  title,
  placeholder = "Klicken zum Auswählen",
  searchFields,
  renderItem,
  displayValue,
  disabled = false,
  size = "middle",
  style,
  allowClear = true,
  onClear,
}: SearchSelectButtonProps<T>) => {
  const [modalVisible, setModalVisible] = useState(false);

  // Helper function to safely convert value to string
  const safeStringValue = (val: any): string => {
    if (val === null || val === undefined) {
      return '';
    }
    if (typeof val === 'object') {
      console.warn('⚠️ SearchSelectButton received object as value:', val);
      // Try to extract common ID fields
      if ('id' in val) {
        return String(val.id);
      }
      if ('uid' in val) {
        return String(val.uid);
      }
      // Fallback to empty string to prevent React error
      return '';
    }
    return String(val);
  };

  const handleSelect = (item: T) => {
    if (onChange) {
      onChange(item.id, item);
    }
    setModalVisible(false);
    
    // Force form update - trigger blur event
    setTimeout(() => {
      const event = new Event('blur', { bubbles: true });
      if (document.activeElement) {
        document.activeElement.dispatchEvent(event);
      }
    }, 100);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onChange) {
      onChange(undefined, undefined as any);
    }
    if (onClear) {
      onClear();
    }
    
    // Force form update - trigger blur event
    setTimeout(() => {
      const event = new Event('blur', { bubbles: true });
      if (document.activeElement) {
        document.activeElement.dispatchEvent(event);
      }
    }, 100);
  };

  // Ensure we have a safe string value for display
  const safeDisplayValue = displayValue || safeStringValue(value);

  return (
    <>
      <Button
        onClick={() => !disabled && setModalVisible(true)}
        disabled={disabled}
        size={size}
        style={{ 
          width: "100%", 
          textAlign: "left",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          ...style 
        }}
      >
        <Space>
          {value ? (
            <>
              <Text ellipsis style={{ maxWidth: "300px" }}>
                {safeDisplayValue}
              </Text>
              {allowClear && (
                <Button
                  type="text"
                  size="small"
                  onClick={handleClear}
                  style={{ padding: "0 4px" }}
                >
                  ✕
                </Button>
              )}
            </>
          ) : (
            <Text type="secondary">{placeholder}</Text>
          )}
        </Space>
        <SearchOutlined />
      </Button>

      <ModernSearchModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        onSelect={handleSelect}
        resource={resource}
        title={title}
        searchFields={searchFields}
        renderItem={renderItem}
        placeholder={`${title} suchen...`}
      />
    </>
  );
}; 